'use client'
import React, { useState } from "react";
import { Clock, Users, Globe, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TContactConfig, } from "@/app/config";
import { toast } from "sonner"

type FormData = {
  name: string;
  number: string;
  email: string;
  category: string;
  message: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

export const ContactSection = ({ config }: { config: TContactConfig['contact'] }) => {

  const featuresIcons = [
    {
      icon: Clock,

    },
    {
      icon: Users,

    },
    {
      icon: Globe,

    },
  ];


  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const [formData, setFormData] = useState<FormData>({
    name: "",
    number: "",
    email: "",
    category: "",
    message: "",
  });


  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 4) {
      newErrors.name = "Name must be at least 4 characters long";
    }

    if (!formData.number.trim()) {
      newErrors.number = "Number is required";
    } else if (!/^\d+$/.test(formData.number.trim())) {
      newErrors.number = "Please enter a valid number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const accessKey = process.env.NEXT_PUBLIC_ACCESS_KEY;

      if (!accessKey) {
        throw new Error("Access key not found");
      }
      const payload = {
        public_access_key: accessKey,
        content: {
          Name: formData.name,
          Number: formData.number,
          Email: formData.email,
          Category: formData.category,
          Message: formData.message,
        },
      };

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_ACCESS_API}/collection/data/public`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        toast.success("Form submitted successfully!");
        setFormData({
          name: "",
          email: "",
          number: "",
          category: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="w-full py-12 md:py-20 lg:py-[84px] bg-white flex justify-center"
      id="contact"
    >
      <div className="max-w-[1120px] w-full px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-14 items-start">
          {/* Left Content Section */}
          <div className="flex flex-col gap-6 md:gap-8 lg:gap-[20.3px] ">
            <header className="flex flex-col gap-4 lg:gap-0">
              <h2 className="font-normal text-black text-2xl md:text-3xl lg:text-[31.5px] leading-tight lg:leading-[35px]">
                {config.title}
              </h2>
            </header>

            <p className="font-normal text-[#495565] text-sm md:text-base lg:text-[15.7px] leading-relaxed lg:leading-[24.5px]">
              {config.description}
            </p>

            <div className="flex flex-col gap-4 md:gap-6 lg:gap-[21px]">
              {config.features.map((feature, index) => {
                const Icon = featuresIcons[index].icon;
                return (
                  <div
                    key={index}
                    className="flex items-start gap-3 md:gap-4 lg:gap-3.5"
                  >
                    <div
                      className={`flex w-10 h-10 md:w-12 md:h-12 lg:w-[42px] lg:h-[42px] items-center justify-center ${feature.bgColor} rounded-lg`}
                    >
                      <Icon
                        className={`w-5 h-5 md:w-6 md:h-6 lg:w-[21px] lg:h-[21px] ${feature.iconColor}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1 md:gap-2 lg:gap-[7px]">
                      <h3 className="text-black text-base md:text-lg lg:text-sm leading-tight">
                        {feature.title}
                      </h3>
                      <p className="text-[#495565] text-sm md:text-base lg:text-[13.9px] leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Form */}
          <Card className="bg-[#f8fafb] rounded-xl lg:rounded-[12.75px] border">
            <CardContent className="p-6 md:p-8 lg:p-[29px]">
              {/* Success/Error Messages */}
              <div className="mb-4">
                {submitStatus === "success" && (
                  <div className="p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm">
                    Message sent successfully! We&apos;ll contact you soon.
                  </div>
                )}
                {submitStatus === "error" && (
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                    Sorry, there was an error. Please try again.
                  </div>
                )}
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 md:gap-6 lg:gap-[21px]"
              >
                {/* Name & Number */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label>
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="Enter your name (minimum 4 characters)"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className={`${errors.name ? "border-red-500" : "border-[#d0d5db]"
                        }`}
                      disabled={isSubmitting}
                    />
                    {errors.name && (
                      <span className="text-red-500 text-xs">
                        {errors.name}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>
                      Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="Enter your number"
                      value={formData.number}
                      onChange={(e) =>
                        handleInputChange("number", e.target.value)
                      }
                      className={`${errors.number ? "border-red-500" : "border-[#d0d5db]"
                        }`}
                      disabled={isSubmitting}
                    />
                    {errors.number && (
                      <span className="text-red-500 text-xs">
                        {errors.number}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <Label>
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={`${errors.email ? "border-red-500" : "border-[#d0d5db]"
                      }`}
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <span className="text-red-500 text-xs">{errors.email}</span>
                  )}
                </div>

                {/* Category */}
                <div className="flex flex-col gap-2">
                  <Label>Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                    className="border border-[#d0d5db] rounded-lg p-2 disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    <option value="">Select your category</option>
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="working_professional">
                      Working Professional
                    </option>
                  </select>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <Label>Message</Label>
                  <Textarea
                    placeholder="Please describe your question or issue..."
                    value={formData.message}
                    onChange={(e) =>
                      handleInputChange("message", e.target.value)
                    }
                    disabled={isSubmitting}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="bg-[#ffbf00] hover:bg-[#ffbf00]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-white">Sending...</span>
                    </div>
                  ) : (
                    <>
                      <Send className="w-4 h-4 text-white" />
                      <span className="text-white">Send Message</span>
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};